import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TRYON_SERVER_URL } from "../config";
import { selectAccessToken, selectAuthenticated, selectUserData } from "../selectors";
import { setUserData } from "../store/AuthActions";
import { modelSwapCredits, swapFashionModel } from "../Utils/ModelUtils";
import { fetchUser } from "../Utils/UserUtils";
import CanvasLoader from "../components/CanvasLoader";
import Joyride from "react-joyride";
import { useJoyrideStyles } from "../theme";
import { TOURS, TOUR_PROPS, useTour, withoutBeacon } from "../Utils/tours";

const TOUR_STEPS = withoutBeacon([
    { target: ".swap-photo", content: "Add the photo whose person you want to replace." },
    { target: ".swap-prompt", content: "Optional: describe the new person, or add a face photo below. The new model follows it." },
    { target: ".swap-go", content: "Swap starts the run. The button shows the credits it will use." },
    { target: ".swap-result", content: "Your result shows up here, with earlier swaps below it." },
]);

const imageSrc = (path) => {
    if (!path) return "";
    return path.startsWith("http") ? path : `${TRYON_SERVER_URL}/${path}`;
};

export default function ModelGenerationAI() {
    const [sourcePreview, setSourcePreview] = useState(null);
    const [sourceFile, setSourceFile] = useState(null);
    const [facePreview, setFacePreview] = useState(null);
    const [faceFile, setFaceFile] = useState(null);
    const [prompt, setPrompt] = useState("");
    const [resultImage, setResultImage] = useState("");
    const [experiments, setExperiments] = useState([]);
    const [experimentsFetched, setExperimentsFetched] = useState(false);
    const [isSwapping, setIsSwapping] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const sourceInputRef = useRef(null);
    const faceInputRef = useRef(null);
    const accessToken = useSelector(selectAccessToken) || null;
    const authenticated = useSelector(selectAuthenticated);
    const tourStyles = useJoyrideStyles();
    const { done: tourDone, onCallback: onTourCallback } = useTour(TOURS.MODEL_SWAP);
    const userData = JSON.parse(useSelector(selectUserData) || "null");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const credits = modelSwapCredits(Boolean(faceFile));

    const sourcePreviewRef = useRef(null);
    const facePreviewRef = useRef(null);
    sourcePreviewRef.current = sourcePreview;
    facePreviewRef.current = facePreview;

    useEffect(() => {
        return () => {
            if (sourcePreviewRef.current) URL.revokeObjectURL(sourcePreviewRef.current);
            if (facePreviewRef.current) URL.revokeObjectURL(facePreviewRef.current);
        };
    }, []);

    useEffect(() => {
        if (!experimentsFetched && (authenticated || accessToken)) {
            fetchExperiments();
        }
    }, [authenticated, accessToken, experimentsFetched]);

    const fetchExperiments = async () => {
        if (!accessToken) return;
        const response = await fetch(`${TRYON_SERVER_URL}/api/v1/experiment/?aimodel=model_swap&access=private`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
        if (response.ok) {
            const res = await response.json();
            setExperiments(Array.isArray(res) ? res : []);
        }
        setExperimentsFetched(true);
    };

    const readFile = (event, setPreview, setFile) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file || !file.type.startsWith("image/")) {
            setErrorMessage("Choose an image file");
            return;
        }
        setErrorMessage("");
        setPreview((current) => {
            if (current) URL.revokeObjectURL(current);
            return URL.createObjectURL(file);
        });
        setFile(file);
    };

    const clearSource = () => {
        setSourcePreview((current) => {
            if (current) URL.revokeObjectURL(current);
            return null;
        });
        setSourceFile(null);
    };

    const clearFace = () => {
        setFacePreview((current) => {
            if (current) URL.revokeObjectURL(current);
            return null;
        });
        setFaceFile(null);
    };

    const handleSwap = async () => {
        if (!authenticated) {
            navigate("/signin");
            return;
        }
        if (!sourceFile) {
            setErrorMessage("Upload a photo to swap");
            return;
        }

        const balance = Number(userData?.credits);
        if (!Number.isFinite(balance) || balance < credits) {
            navigate(`/credits?buy_credits=true&num_of_credits=${credits}`);
            return;
        }

        setIsSwapping(true);
        setErrorMessage("");

        const result = await swapFashionModel({
            sourceFile,
            prompt,
            faceFile,
            accessToken,
        });

        if (result.status !== "success" || !result.data?.result?.image_url) {
            setErrorMessage(result.message || "Failed to swap the model");
            setIsSwapping(false);
            return;
        }

        setResultImage(imageSrc(result.data.result.image_url));
        setPrompt("");
        setExperiments((current) => [result.data, ...current.filter((item) => item.id !== result.data.id)]);
        setIsSwapping(false);

        const user = await fetchUser(accessToken);
        if (user.status === "success") {
            dispatch(setUserData(JSON.stringify(user.data)));
        }
    };

    const earlier = experiments.filter((experiment) => imageSrc(experiment?.result?.image_url) !== resultImage);

    return (
        <>
        {!tourDone ? (
            <Joyride {...TOUR_PROPS} steps={TOUR_STEPS} callback={onTourCallback} styles={tourStyles} />
        ) : null}
        <div className="grid h-[calc(100vh-5rem)] w-full grid-cols-1 gap-4 overflow-y-auto bg-ground p-4 selection:bg-primary-100 lg:grid-cols-12 lg:overflow-hidden lg:min-h-0">
            <div className="flex min-h-[28rem] flex-col rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)] lg:col-span-5 lg:h-full lg:min-h-0 lg:overflow-hidden">
                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
                    <div className="swap-photo">
                        <h2 className="mb-2 font-Mulish font-semibold text-lg text-secondary">Photo</h2>
                        <p className="mb-3 font-Mulish text-sm text-graphite">The person in this photo is replaced.</p>
                        {sourcePreview ? (
                            <div className="relative rounded-2xl bg-primary-50 p-2">
                                <div className="flex h-40 items-center justify-center overflow-hidden rounded-lg bg-white">
                                    <img src={sourcePreview} alt="Photo to swap" className="max-h-full max-w-full object-contain" />
                                </div>
                                <button
                                    type="button"
                                    onClick={clearSource}
                                    className="absolute right-4 top-4 rounded-full border border-hairline bg-white p-2"
                                    aria-label="Remove photo"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => sourceInputRef.current?.click()}
                                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-50 px-4 py-8 font-Mulish text-sm text-graphite hover:bg-primary-100"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Upload a photo
                            </button>
                        )}
                        <input
                            ref={sourceInputRef}
                            type="file"
                            accept="image/*"
                            onChange={(event) => readFile(event, setSourcePreview, setSourceFile)}
                            className="hidden"
                        />
                    </div>

                    <div className="swap-prompt">
                        <label htmlFor="swap-prompt" className="mb-2 block font-Mulish font-semibold text-lg text-secondary">
                            Prompt
                        </label>
                        <p className="mb-3 font-Mulish text-sm text-graphite">Optional. Describe the person who should appear.</p>
                        <textarea
                            id="swap-prompt"
                            rows="3"
                            value={prompt}
                            onChange={(event) => setPrompt(event.target.value)}
                            placeholder="Age, hair, and expression."
                            className="h-24 w-full resize-none rounded-lg border border-hairline bg-white px-3 py-2 font-Mulish text-sm text-secondary outline-none focus:ring-2 focus:ring-primary-200"
                        />
                    </div>

                    <div>
                        <h2 className="mb-2 font-Mulish font-semibold text-lg text-secondary">Face photo</h2>
                        <p className="mb-3 font-Mulish text-sm text-graphite">Optional. The new model follows this face.</p>
                        {facePreview ? (
                            <div className="relative rounded-2xl bg-primary-50 p-2">
                                <div className="flex h-32 items-center justify-center overflow-hidden rounded-lg bg-white">
                                    <img src={facePreview} alt="Face photo" className="max-h-full max-w-full object-contain" />
                                </div>
                                <button
                                    type="button"
                                    onClick={clearFace}
                                    className="absolute right-4 top-4 rounded-full border border-hairline bg-white p-2"
                                    aria-label="Remove face photo"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => faceInputRef.current?.click()}
                                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-50 px-4 py-8 font-Mulish text-sm text-graphite hover:bg-primary-100"
                            >
                                Upload a face photo
                            </button>
                        )}
                        <input
                            ref={faceInputRef}
                            type="file"
                            accept="image/*"
                            onChange={(event) => readFile(event, setFacePreview, setFaceFile)}
                            className="hidden"
                        />
                    </div>

                    {errorMessage ? (
                        <div className="border-l-2 border-primary-500 py-1 pl-3 font-Mulish text-sm text-secondary">
                            {errorMessage}
                        </div>
                    ) : null}
                </div>

                <button
                    type="button"
                    onClick={handleSwap}
                    disabled={isSwapping}
                    className={`swap-go mt-4 w-full shrink-0 rounded-full px-4 py-2.5 font-Mulish font-semibold text-sm ${isSwapping ? "cursor-not-allowed bg-primary-100 text-primary-800" : "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"}`}
                >
                    {isSwapping ? "Swapping..." : `Swap (${credits} credits)`}
                </button>
            </div>

            <div className="swap-result flex min-h-[20rem] flex-col rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)] lg:col-span-7 lg:h-full lg:min-h-0 lg:overflow-hidden">
                <h2 className="mb-3 shrink-0 font-Mulish font-semibold text-lg text-secondary">Result</h2>
                {authenticated === null ? (
                    <div className="flex min-h-[20rem] flex-1 items-center justify-center rounded-2xl bg-primary-50">
                        <CanvasLoader title="Loading earlier swaps" />
                    </div>
                ) : authenticated ? (
                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
                        {isSwapping ? (
                            <div className="flex min-h-[16rem] flex-1 items-center justify-center rounded-2xl bg-primary-50 p-4">
                                <CanvasLoader title="Swapping the model" />
                            </div>
                        ) : resultImage ? (
                            <div className="flex min-h-[16rem] flex-1 items-center justify-center overflow-hidden rounded-2xl bg-primary-50 p-4">
                                <img src={resultImage} alt="Swapped model" className="max-h-full max-w-full rounded-lg object-contain" />
                            </div>
                        ) : !experimentsFetched ? (
                            <div className="flex min-h-[16rem] flex-1 items-center justify-center rounded-2xl bg-primary-50 p-4">
                                <CanvasLoader title="Loading earlier swaps" />
                            </div>
                        ) : (
                            <div className={`flex flex-col items-center justify-center rounded-2xl bg-primary-50 p-6 text-center ${earlier.some((experiment) => experiment?.result?.image_url) ? "shrink-0 py-8" : "min-h-[16rem] flex-1"}`}>
                                <svg className="h-10 w-10 text-primary-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <p className="mt-2 font-Mulish font-semibold text-lg text-secondary">No swap yet</p>
                                <p className="mt-1 font-Mulish text-sm text-graphite">Your model will show up here</p>
                            </div>
                        )}

                        {earlier.some((experiment) => experiment?.result?.image_url) ? (
                            <div className="shrink-0">
                                <p className="mb-2 font-Mulish text-sm font-semibold text-secondary">Earlier swaps</p>
                                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                                    {earlier.filter((experiment) => experiment?.result?.image_url).map((experiment) => (
                                        <button
                                            key={experiment.id}
                                            type="button"
                                            onClick={() => setResultImage(imageSrc(experiment.result.image_url))}
                                            className="relative aspect-[3/4] overflow-hidden rounded-lg bg-white shadow-[0_0_0_1px_var(--hairline)]"
                                        >
                                            <img
                                                src={imageSrc(experiment.result.image_url)}
                                                alt="Earlier swap"
                                                className="h-full w-full object-cover object-top"
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <div className="flex min-h-[16rem] flex-1 flex-col items-center justify-center rounded-2xl bg-primary-50 p-6 text-center">
                        <svg className="h-10 w-10 text-primary-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="mt-2 font-Mulish font-semibold text-lg text-secondary">No swap yet</p>
                        <p className="mt-1 font-Mulish text-sm text-graphite">Your model will show up here</p>
                    </div>
                )}
            </div>
        </div>
        </>
    );
}
